import { actions } from "./slice";
import { db } from "initializer";
import { createSubscribeCollection } from "../firestoreModuleUtils/operators";
import {
  getRoomEffectIdsByText,
  getRoomOrderdEffectIds,
  getRoomEffectById,
} from "./selectors";
import { UpdateEffect, EffectRecord, Effect } from "./records";
import { DefaultRootState, DefaultThunk } from "stores";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  orderBy,
  query,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { addUndo } from "../entities.room.histories/slice";
import {
  calcOrderAppendingToTail,
  reorderEntities,
  undoRedoReorderEntities,
} from "../firestoreModuleUtils/ordaring";
import { getSoundUrl, sendSoundEventLog } from "api-sound-master";
import {
  isUrlExpiringSoon,
  displaySoundMasterErrorMessage,
} from "../entities.rooms/operations";
import { getCurrentRoom } from "../entities.rooms/selectors";

export const effectsRef = (roomId: string) =>
  collection(db, "rooms", roomId, "effects");

// Helper function to resolve soundUrl from soundRef for Effect
const resolveEffectUrlFromRef = async (
  data: UpdateEffect,
  current?: Effect | null,
  token?: string
): Promise<{ resolved: UpdateEffect; error?: string }> => {
  const resolved = { ...data };
  let error: string | undefined;

  // Determine which soundRef to use: new one from data, or existing one from current
  const soundRef =
    data.soundRef !== undefined ? data.soundRef : current?.soundRef;

  // Resolve soundUrl from soundRef if soundRef exists
  if (soundRef) {
    // Determine which soundUrl to check: new one from data, or existing one from current
    const soundUrl =
      data.soundUrl !== undefined ? data.soundUrl : current?.soundUrl ?? null;

    // Check if soundRef changed
    const refChanged =
      data.soundRef !== undefined && data.soundRef !== current?.soundRef;

    if (refChanged) {
      // Ref changed: check if data already has a valid URL
      if (data.soundUrl && !isUrlExpiringSoon(data.soundUrl)) {
        // data.soundUrl is valid, use it as-is
        resolved.soundUrl = data.soundUrl;
      } else {
        // Fetch new URL if data.soundUrl is missing or expiring soon
        const url = await getSoundUrl(soundRef, token);
        if (url) {
          resolved.soundUrl = url;
        } else {
          error = "failed_to_get_soundmaster_url";
        }
      }
    } else {
      // Same Ref: only fetch new URL if current URL is expiring soon
      if (isUrlExpiringSoon(soundUrl)) {
        const url = await getSoundUrl(soundRef, token);
        if (url) {
          resolved.soundUrl = url;
        } else {
          error = "failed_to_get_soundmaster_url";
        }
      } else if (soundUrl) {
        // Keep the current URL if it's still valid
        resolved.soundUrl = soundUrl;
      }
    }

    // Also preserve the soundRef if it wasn't in data
    if (data.soundRef === undefined) {
      resolved.soundRef = soundRef;
    }
  }

  return { resolved, error };
};

export const subscribeRoomEffects = createSubscribeCollection(
  actions,
  (roomId: string) => query(effectsRef(roomId), orderBy("order"))
);

export const addRoomEffect =
  (roomId: string, uid: string): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const ids = getRoomOrderdEffectIds(state);
    const data = EffectRecord({
      owner: uid,
      order: calcOrderAppendingToTail(ids, state.entities.roomEffects.entities),
      active: false,
    });
    const doc = await addDoc(effectsRef(roomId), data);
    dispatch(
      addUndo({
        kind: "update-effect",
        id: doc.id,
        before: null,
        after: data,
      })
    );
  };
export const updateRoomEffectName =
  (roomId: string, effectId: string, item: UpdateEffect): DefaultThunk =>
  () => {
    return setDoc(
      doc(effectsRef(roomId), effectId),
      {
        name: item.name,
      },
      { merge: true }
    );
  };
export const updateRoomEffect =
  (roomId: string, effectId: string, item: UpdateEffect): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const currentEffect = getRoomEffectById(state, effectId);
    const currentRoom = getCurrentRoom(state);

    // Resolve soundRef to URL when playing
    const result = await resolveEffectUrlFromRef(
      item,
      currentEffect,
      currentRoom?.soundMasterToken || undefined
    );

    // Send event log if playTime is being updated and soundRef exists
    if (
      item.playTime !== undefined &&
      currentEffect?.soundRef &&
      !result.error
    ) {
      sendSoundEventLog("play_effect", currentEffect.soundRef);
    }

    // Show error notification if URL resolution failed during playback
    if (result.error) {
      dispatch(displaySoundMasterErrorMessage());
    }

    return setDoc(
      doc(effectsRef(roomId), effectId),
      {
        ...result.resolved,
        updatedAt: Date.now(),
      },
      { merge: true }
    );
  };
export const updateCurrentRoomEffect =
  (item: UpdateEffect): DefaultThunk =>
  async (_, getState) => {
    const state = getState();
    const roomId = state.app.state.roomId;
    const effectId = state.app.state.openRoomEffectId;
    if (!roomId || !effectId) return null;

    const currentEffect = getRoomEffectById(state, effectId);
    const currentRoom = getCurrentRoom(state);
    const result = await resolveEffectUrlFromRef(
      item,
      currentEffect,
      currentRoom?.soundMasterToken || undefined
    );
    // playTime ではなく内容の更新系のためここではイベント送信をしていない
    // エフェクト編集時はエラー通知を表示しない（再生時ではないため）

    return setDoc(
      doc(effectsRef(roomId), effectId),
      {
        ...result.resolved,
        updatedAt: Date.now(),
      },
      { merge: true }
    );
  };
export const activeRoomEffectByText =
  (roomId: string, text: string): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const effectIds = getRoomEffectIdsByText(state, { text });
    const currentRoom = getCurrentRoom(state);

    return Promise.all(
      effectIds.map(async (effectId) => {
        const effect = getRoomEffectById(state, effectId);

        // Resolve soundRef to URL when playing
        const result = await resolveEffectUrlFromRef(
          { playTime: Date.now() },
          effect,
          currentRoom?.soundMasterToken || undefined
        );

        // Send event log if effect has soundRef
        if (effect?.soundRef && !result.error) {
          sendSoundEventLog("play_effect", effect.soundRef);
        }

        // Show error notification if URL resolution failed during playback
        if (result.error) {
          dispatch(displaySoundMasterErrorMessage());
        }

        return setDoc(
          doc(effectsRef(roomId), effectId),
          {
            ...result.resolved,
            updatedAt: Date.now(),
          },
          { merge: true }
        );
      })
    );
  };
export const deleteRoomEffect = (roomId: string, effectId: string) => () => {
  return deleteDoc(doc(effectsRef(roomId), effectId));
};

export const reorderEffects = reorderEntities({
  selectOrderdIds: getRoomOrderdEffectIds,
  selectEntities: (state: DefaultRootState) =>
    state.entities.roomEffects.entities,
  actionReorder: actions.reorder,
  actionUpdateOrder: actions.updateOrder,
  collectionRef: effectsRef,
  type: "effect",
});

export const undoRedoReorderEffects = undoRedoReorderEntities({
  selectOrderdIds: getRoomOrderdEffectIds,
  selectEntities: (state: DefaultRootState) =>
    state.entities.roomEffects.entities,
  actionReorder: actions.reorder,
  actionUpdateOrder: actions.updateOrder,
  collectionRef: effectsRef,
});

export const importRoomEffects =
  (effectsData: { [effectId: string]: UpdateEffect }): DefaultThunk =>
  (_, getState) => {
    const roomId = getState().app.state.roomId;
    if (!roomId) return null;
    const effectIds = Object.keys(effectsData);
    const batch = writeBatch(db);
    const ref = effectsRef(roomId);
    effectIds.forEach((effectId) => {
      batch.set(doc(ref, effectId), EffectRecord(effectsData[effectId]));
    });
    return batch.commit();
  };
